import React, { useMemo, useCallback } from "react";
import {
  Typography,
  Box,
  IconButton,
  Tooltip,
  Paper,
  Chip,
  Stack,
  useTheme,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import {
  Person as PersonIcon,
  Home as HomeIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  ConfirmationNumber as ConfirmationNumberIcon,
  Notes as NotesIcon,
} from "@mui/icons-material";
import { Reservation } from "../types/Reservation";
import { UserPermissions } from "../types/interfaces";
import { formatReservaFechas } from "../styles/reservationStyles";
import { formatCurrency } from "../../shared/utils/formatters";
import { formatDateKey } from "../../shared/utils/dateUtils";
import EstadiasPreviasChip from "./EstadiasPreviasChip";

export interface ReservationCardsProps {
  reservations: Reservation[];
  onEdit: (reservation: Reservation) => void;
  onDelete: (reservation: Reservation) => void;
  onVoucher: (reservation: Reservation) => void;
  permissions: UserPermissions;
  searchTerm?: string;
}

const getNoches = (entrada: string, salida: string): number => {
  const entradaDate = new Date(`${entrada}T00:00:00`);
  const salidaDate = new Date(`${salida}T00:00:00`);
  return Math.max(
    0,
    Math.round((salidaDate.getTime() - entradaDate.getTime()) / 86400000)
  );
};

const getTemporalStatus = (entrada: string, salida: string) => {
  const today = formatDateKey(new Date());

  if (entrada === today) {
    return { label: "Entra hoy", color: "info" as const };
  }

  if (salida === today) {
    return { label: "Sale hoy", color: "warning" as const };
  }

  if (entrada > today) {
    return { label: "Próxima", color: "default" as const };
  }

  if (salida < today) {
    return { label: "Finalizada", color: "default" as const };
  }

  return { label: "En curso", color: "success" as const };
};

const ReservationCards: React.FC<ReservationCardsProps> = React.memo(
  ({
    reservations,
    onEdit,
    onDelete,
    onVoucher,
    permissions,
    searchTerm = "",
  }) => {
    const theme = useTheme();

    const processedReservations = useMemo(() => {
      return reservations.map((reservation: Reservation) => {
        const fechas = formatReservaFechas(
          reservation.entrada,
          reservation.salida
        );
        const zonaColor =
          parseInt(reservation.departamento) <= 18
            ? theme.palette.zonas.centro.border
            : theme.palette.zonas.loma.border;

        return {
          ...reservation,
          fechas,
          zonaColor,
          noches: getNoches(reservation.entrada, reservation.salida),
          temporalStatus: getTemporalStatus(
            reservation.entrada,
            reservation.salida
          ),
          valorTotal: Number(reservation.valor_total) || 0,
          pagoTotal: Number(reservation.pago) || 0,
        };
      });
    }, [
      reservations,
      theme.palette.zonas.centro.border,
      theme.palette.zonas.loma.border,
    ]);

    const handleEdit = useCallback(
      (reservation: Reservation) => {
        onEdit(reservation);
      },
      [onEdit]
    );

    const handleDelete = useCallback(
      (reservation: Reservation) => {
        onDelete(reservation);
      },
      [onDelete]
    );

    const handleVoucher = useCallback(
      (reservation: Reservation) => {
        onVoucher(reservation);
      },
      [onVoucher]
    );

    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: 1,
          flex: 1,
          minHeight: 0,
          overflowY: "auto",
        }}
      >
        {reservations.length === 0 ? (
          <Paper sx={{ p: 2, textAlign: "center" }}>
            <Typography variant="body2" color="text.secondary">
              {searchTerm ? "No se encontraron reservas" : "No hay reservas"}
            </Typography>
          </Paper>
        ) : (
          processedReservations.map((reservation) => {
            const { fechas, zonaColor } = reservation;
            const saldo = reservation.valorTotal - reservation.pagoTotal;
            const hasNotes = Boolean(reservation.notas?.trim());

            return (
              <Paper
                key={reservation.id}
                elevation={2}
                sx={{
                  p: 1.5,
                  borderRadius: 1,
                  bgcolor: theme.palette.background.paper,
                  color: theme.palette.text.primary,
                  border: "1px solid",
                  borderColor: "divider",
                  boxShadow: "none",
                  transition: "border-color 0.2s, background-color 0.2s",
                  cursor: permissions.canEditReservations ? "pointer" : "default",
                  "&:hover": {
                    borderColor: alpha(zonaColor, 0.45),
                    bgcolor: alpha(zonaColor, 0.04),
                  },
                }}
                onClick={() => {
                  if (permissions.canEditReservations) {
                    onEdit(reservation);
                  }
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "flex-start",
                  }}
                >
                  <Box sx={{ flex: 1 }}>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        mb: 1,
                      }}
                    >
                      <PersonIcon
                        sx={{
                          fontSize: "1.2rem",
                          color: theme.palette.text.secondary,
                        }}
                      />
                      <Typography
                        variant="subtitle1"
                        fontWeight="bold"
                      >
                        {reservation.nombre} {reservation.apellido}
                      </Typography>
                    </Box>
                    {reservation.estadias_previas ? (
                      <Box sx={{ mb: 1 }}>
                        <EstadiasPreviasChip
                          maxDeptos={2}
                          datos={{
                            cantidad: reservation.estadias_previas.cantidad,
                            departamentos:
                              reservation.estadias_previas.departamentos,
                            numeroDeEstadia:
                              reservation.estadias_previas.cantidad + 1,
                            cargando: false,
                          }}
                        />
                      </Box>
                    ) : null}

                    <Stack direction="row" spacing={1} sx={{ mb: 1 }} useFlexGap flexWrap="wrap">
                      <Chip
                        label={`#${reservation.id}`}
                        size="small"
                        variant="outlined"
                        sx={{ color: "text.secondary" }}
                      />
                      <Chip
                        icon={<HomeIcon />}
                        label={`Depto ${reservation.departamento}`}
                        size="small"
                        sx={{
                          bgcolor: alpha(zonaColor, 0.12),
                          border: "1px solid",
                          borderColor: alpha(zonaColor, 0.28),
                          fontWeight: 700,
                        }}
                      />
                      {reservation.cliente?.origen_reserva &&
                        reservation.cliente.origen_reserva !== "directo" && (
                          <Chip
                            label={reservation.cliente.origen_reserva}
                            size="small"
                            variant="outlined"
                            sx={{ textTransform: "capitalize" }}
                          />
                        )}
                      {hasNotes && (
                        <Chip
                          icon={<NotesIcon />}
                          label="Tiene notas"
                          size="small"
                          variant="outlined"
                          sx={{ color: "text.secondary" }}
                        />
                      )}
                    </Stack>

                    <Typography variant="body2" color="text.secondary" mb={1}>
                      {fechas.entrada.fecha} - {fechas.salida.fecha} ·{" "}
                      {reservation.noches}{" "}
                      {reservation.noches === 1 ? "noche" : "noches"}
                    </Typography>

                    <Chip
                      label={reservation.temporalStatus.label}
                      color={reservation.temporalStatus.color}
                      variant="outlined"
                      size="small"
                      sx={{ mb: 1 }}
                    />

                    <Typography variant="body2" color="text.secondary" mb={1}>
                      Documento: {reservation.dni || "-"}
                    </Typography>

                    <Stack direction="row" spacing={1} alignItems="center">
                      <Typography variant="body2" fontWeight={700}>
                        {formatCurrency(reservation.valorTotal)}
                      </Typography>
                      <Chip
                        label={
                          saldo > 0
                            ? `Saldo ${formatCurrency(saldo)}`
                            : saldo < 0
                            ? `A favor ${formatCurrency(Math.abs(saldo))}`
                            : "Saldada"
                        }
                        color={saldo > 0 ? "warning" : "success"}
                        variant={saldo > 0 ? "filled" : "outlined"}
                        size="small"
                      />
                    </Stack>
                  </Box>

                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      gap: 0.5,
                      ml: 1,
                    }}
                  >
                    {permissions.canEditReservations && (
                      <Tooltip title="Editar" placement="left">
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleEdit(reservation);
                          }}
                          sx={{
                            bgcolor: alpha(theme.palette.primary.main, 0.1),
                            "&:hover": {
                              bgcolor: alpha(theme.palette.primary.main, 0.2),
                            },
                            minWidth: { xs: "44px", sm: "auto" },
                            minHeight: { xs: "44px", sm: "auto" },
                            padding: { xs: "12px", sm: "8px" },
                            touchAction: "manipulation",
                            "&:active": {
                              transform: "scale(0.95)",
                            },
                          }}
                        >
                          <EditIcon
                            sx={{
                              fontSize: { xs: "1.3rem", sm: "1.1rem" },
                              color: theme.palette.primary.main,
                            }}
                          />
                        </IconButton>
                      </Tooltip>
                    )}

                    {permissions.canDeleteReservations && (
                      <Tooltip title="Eliminar" placement="left">
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDelete(reservation);
                          }}
                          sx={{
                            color: theme.palette.text.secondary,
                            "&:hover": {
                              color: theme.palette.error.main,
                              bgcolor: alpha(theme.palette.error.main, 0.08),
                            },
                            minWidth: { xs: "44px", sm: "auto" },
                            minHeight: { xs: "44px", sm: "auto" },
                            padding: { xs: "12px", sm: "8px" },
                            touchAction: "manipulation",
                            "&:active": {
                              transform: "scale(0.95)",
                            },
                          }}
                        >
                          <DeleteIcon
                            sx={{
                              fontSize: { xs: "1.3rem", sm: "1.1rem" },
                            }}
                          />
                        </IconButton>
                      </Tooltip>
                    )}

                    <Tooltip title="Ver comprobante" placement="left">
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleVoucher(reservation);
                        }}
                        sx={{
                          bgcolor: alpha(theme.palette.success.main, 0.1),
                          "&:hover": {
                            bgcolor: alpha(theme.palette.success.main, 0.2),
                          },
                          minWidth: { xs: "44px", sm: "auto" },
                          minHeight: { xs: "44px", sm: "auto" },
                          padding: { xs: "12px", sm: "8px" },
                          touchAction: "manipulation",
                          "&:active": {
                            transform: "scale(0.95)",
                          },
                        }}
                      >
                        <ConfirmationNumberIcon
                          sx={{
                            fontSize: { xs: "1.3rem", sm: "1.1rem" },
                            color: theme.palette.success.main,
                          }}
                        />
                      </IconButton>
                    </Tooltip>

                  </Box>
                </Box>
              </Paper>
            );
          })
        )}
      </Box>
    );
  }
);

export default ReservationCards;
